import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, MapPin, Phone, ShieldCheck, Star, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { sendContactForm, normalizeUrl } from "@/utils/emailService";

const CulverHero = () => {
  const [url, setUrl] = useState("");
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const startAudit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!url || url.trim().length < 3) {
      toast({
        title: "Add your website URL",
        description: "Enter your business URL to get your free Culver City audit.",
        variant: "destructive",
      });
      return;
    }
    setOpen(true);
  };

  const submitLead = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    if (!name || !email) {
      toast({ title: "Name and email required", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      const ok = await sendContactForm({
        name,
        email,
        phone,
        website: normalizeUrl(url),
        message: `Culver City instant audit request for ${normalizeUrl(url)}`,
        service: "Culver City SEO Audit",
      });
      if (!ok) throw new Error("submit failed");
      sessionStorage.setItem("formSubmitted", "true");
      navigate("/thank-you");
    } catch {
      toast({
        title: "Something went wrong",
        description: "Please try again or call (323) 784-1577.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="relative overflow-hidden bg-emerald-radial text-primary-foreground">
      {/* Decorative grid */}
      <div className="absolute inset-0 grid-emerald opacity-40 pointer-events-none" />
      {/* Gold orb */}
      <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-accent/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full bg-secondary/30 blur-3xl pointer-events-none" />

      <div className="page-container relative z-10 pt-32 pb-20 md:pt-40 md:pb-28">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Copy */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent/10 border border-accent/30 backdrop-blur-sm mb-6"
            >
              <MapPin className="w-3.5 h-3.5 text-accent" />
              <span className="text-xs font-medium tracking-wide text-accent uppercase">
                Serving Culver City &amp; the West Side
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]"
            >
              Your Culver City competitors are{" "}
              <span className="gold-gradient-text">stealing your customers</span>{" "}
              every single day.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-6 text-lg md:text-xl text-primary-foreground/80 max-w-2xl"
            >
              We rank Culver City businesses on page one of Google in 90 days — or you don't pay.
              Get a free, no-BS audit and see exactly how many leads you're losing right now.
            </motion.p>

            {/* URL form */}
            <motion.form
              onSubmit={startAudit}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-8 max-w-xl"
            >
              <div className="flex flex-col sm:flex-row gap-3 p-2 rounded-2xl bg-background/10 backdrop-blur-md border border-primary-foreground/15">
                <div className="flex-1 flex items-center gap-2 px-3">
                  <span className="text-primary-foreground/60 font-mono text-sm hidden sm:inline">
                    https://
                  </span>
                  <Input
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="yourbusiness.com"
                    className="border-0 bg-transparent text-primary-foreground placeholder:text-primary-foreground/50 text-base h-12 focus-visible:ring-0 focus-visible:ring-offset-0 px-0"
                  />
                </div>
                <Button
                  type="submit"
                  size="lg"
                  className="btn-gold rounded-xl h-12 px-6 font-semibold text-base whitespace-nowrap group"
                >
                  Get Free Audit
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
              <p className="mt-3 text-xs text-primary-foreground/60 flex items-center gap-2">
                <ShieldCheck className="w-3.5 h-3.5 text-accent" />
                Free • No credit card • Results in under 60 seconds
              </p>
            </motion.form>

            {/* Trust strip */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-6"
            >
              {[
                { num: "#1", label: "Culver City SEO rankings" },
                { num: "127+", label: "Local clients ranked" },
                { num: "+312%", label: "Avg. organic traffic" },
                { num: "90 days", label: "Or you don't pay" },
              ].map((s, i) => (
                <div key={i}>
                  <div className="text-2xl md:text-3xl font-bold gold-gradient-text font-heading">
                    {s.num}
                  </div>
                  <div className="text-xs text-primary-foreground/70 mt-1">{s.label}</div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right: SERP mockup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="lg:col-span-5"
          >
            <div className="relative">
              <div className="absolute -inset-4 bg-accent/20 blur-2xl rounded-3xl" />
              <div className="relative bg-card text-card-foreground rounded-2xl shadow-2xl ring-gold overflow-hidden">
                <div className="bg-muted/60 px-4 py-2.5 flex items-center gap-2 border-b border-border">
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
                  </div>
                  <div className="flex-1 mx-3 px-3 py-1 rounded-md bg-background text-xs text-muted-foreground font-mono truncate">
                    google.com/search?q=seo+culver+city
                  </div>
                </div>
                <div className="p-5 space-y-4">
                  <div className="text-xs text-muted-foreground">
                    About 4,820,000 results
                  </div>

                  {/* Top result - your client */}
                  <div className="p-4 rounded-xl bg-gradient-to-br from-accent/10 to-secondary/5 border-2 border-accent/40 relative">
                    <div className="absolute -top-2 -right-2 px-2 py-0.5 bg-accent text-accent-foreground text-[10px] font-bold rounded uppercase tracking-wider">
                      Your client
                    </div>
                    <div className="text-xs text-secondary font-medium mb-1">
                      yourbusiness.com › culver-city
                    </div>
                    <div className="text-base font-semibold text-secondary leading-snug">
                      #1 Top-Rated [Your Service] in Culver City, CA
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 line-clamp-2">
                      Voted Culver City's best. 200+ five-star reviews. Free quote in 60 seconds.
                    </div>
                    <div className="flex items-center gap-1 mt-2">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-3 h-3 fill-accent text-accent" />
                      ))}
                      <span className="text-xs text-muted-foreground ml-1">
                        4.9 (247) · Open now
                      </span>
                    </div>
                  </div>

                  {/* Competitor results - faded */}
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="opacity-40 px-1">
                      <div className="text-xs text-muted-foreground mb-1">
                        competitor-{i}.com
                      </div>
                      <div className="text-sm text-muted-foreground line-clamp-1">
                        Generic SEO Services Culver City — Page {i + 1}
                      </div>
                    </div>
                  ))}

                  <div className="pt-3 border-t border-border flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs text-secondary font-semibold">
                      <TrendingUp className="w-4 h-4" />
                      +47 leads / month
                    </div>
                    <div className="text-xs text-muted-foreground">90 days in</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Phone fallback */}
        <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-4 text-sm text-primary-foreground/80">
          <a
            href="tel:+13237841577"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background/10 border border-primary-foreground/15 backdrop-blur-sm hover:bg-background/20 transition-colors"
          >
            <Phone className="w-4 h-4 text-accent" />
            <span className="font-semibold">Or call (323) 784-1577</span>
          </a>
          <span className="text-primary-foreground/60">
            Free consultation · No long contracts · Local team in Culver City
          </span>
        </div>
      </div>

      {/* Lead capture modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading">
              Where should we send your audit?
            </DialogTitle>
            <DialogDescription>
              We'll analyze <span className="font-semibold text-foreground">{url}</span>{" "}
              and email you a custom Culver City SEO report within 24 hours.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitLead} className="space-y-3 pt-2">
            <Input
              placeholder="Your name *"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="h-11"
            />
            <Input
              type="email"
              placeholder="Work email *"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="h-11"
            />
            <Input
              type="tel"
              placeholder="Phone (for fastest reply)"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="h-11"
            />
            <Button
              type="submit"
              disabled={submitting}
              className="w-full btn-gold h-12 font-semibold text-base"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…
                </>
              ) : (
                <>
                  Send My Free Audit <ArrowRight className="ml-2 w-4 h-4" />
                </>
              )}
            </Button>
            <p className="text-xs text-muted-foreground flex items-center gap-1.5 pt-1">
              <ShieldCheck className="w-3.5 h-3.5" /> 100% private. We never share your info.
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default CulverHero;
